import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {getUserId} from "../../requests/AppSession";
import {NumericFormat} from "react-number-format";

import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label} from "@headlessui/react";
import {useAuth} from "@workos-inc/authkit-react";
import {findUserBuyPurchaseHistory, getUserVenueMonths} from "../requests/AppRequestCustomerUser";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {HistorySkeleton} from "./VTSkeletons";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function VTUserBuyVTHistory() {
    const {isLoading, getAccessToken} = useAuth();

    let navigate = useNavigate();

    const [userId, setUserId] = React.useState(null);
    const [buyHistory, setBuyHistory] = useState(null);

    const [dates, setDates] = useState([]);
    const [datesLoaded, setDatesLoaded] = useState(false);
    const [monthNumber, setMonthNumber] = useState(null);
    const [yearNumber, setYearNumber] = useState(null);

    const [errorMessage, setErrorMessage] = React.useState(null);

    const getDatesSuccess = async responseJson => {
        if (
            responseJson
        ) {
            setDates(responseJson);
            setDatesLoaded(true);
            if (responseJson.length > 0) {
                setSelectedDate(responseJson[0]);
                setMonthNumber(responseJson[0].monthNumber);
                setYearNumber(responseJson[0].yearNumber)
            }
        } else {
            setErrorMessage("Error accessing venue dates, please refresh and verify Internet Connection.");
        }
    };

    const getDatesError = forceLogout => {
        setErrorMessage("Error accessing venue dates, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        getUserId().then(userIdSession => {
            if (userIdSession) {
                setUserId(userIdSession);
            } else {
                navigate('/login/mobile', {replace: true});
            }
        })

        if (!isLoading) {
            getUserVenueMonths(getDatesSuccess, getDatesError, getAccessToken);
        }
        return function cleanup() {

        };
    }, [isLoading]);

    const getSuccess = async responseJson => {
        if (
            responseJson
        ) {
            setBuyHistory(responseJson);
        } else {
            setErrorMessage("Error accessing user purchase history, please refresh and verify Internet Connection.");
        }
    };

    const getError = forceLogout => {
        setErrorMessage("Error accessing user purchase history, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (!isLoading && userId !== null && monthNumber !== null && yearNumber !== null) {
            // Reset so the spinner shows and the previous month's purchases don't linger on screen
            // while the new month loads.
            setBuyHistory(null);
            findUserBuyPurchaseHistory(userId, yearNumber, monthNumber, getSuccess, getError, getAccessToken);
        }
        return function cleanup() {

        };
    }, [isLoading, userId, monthNumber, yearNumber]);

    const [query, setQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);

    const filteredDate =
        query === ''
            ? dates
            : dates.filter((date) => {
                return date.name.toLowerCase().includes(query.toLowerCase())
            })

    // Purchases newest-first, independent of the server's response order.
    const sortedHistory = buyHistory
        ? [...buyHistory].sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate))
        : null;

    // Month rollups for the summary card.
    const monthTotalSpent = buyHistory
        ? buyHistory.reduce((sum, h) => sum + (h.price || 0), 0)
        : 0;
    const monthTotalVt = buyHistory
        ? buyHistory.reduce((sum, h) => sum + (h.virtualTips || 0), 0)
        : 0;
    const monthPurchaseCount = buyHistory ? buyHistory.length : 0;

    // Only treat an empty month list as "no history" once the dates fetch has actually
    // completed — otherwise the empty state flashes before data loads.
    const hasNoMonths = datesLoaded && dates.length === 0;

    return (
        <div className="bg-white dark:bg-gray-900">
            <div className="mx-auto max-w-7xl py-16 px-4 sm:px-6 lg:px-8 lg:pb-24">
                <div className="max-w-xl">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-3xl">Purchase History</h1>
                    {/*<p className="mt-2 text-sm text-gray-500">*/}
                    {/*    VirtualTipz purchase history*/}
                    {/*</p>*/}
                </div>

                <div className="mt-16">
                    <h2 className="sr-only">Recent orders</h2>

                    <div className="space-y-8">

                        <Combobox
                            as="div"
                            value={selectedDate}
                            onChange={(value) => {
                                setQuery('')
                                setSelectedDate(value);
                                if (value) {
                                    setMonthNumber(value.monthNumber);
                                    setYearNumber(value.yearNumber);
                                }
                            }}
                        >
                            <Label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Month</Label>
                            <div className="relative mt-2">
                                <ComboboxInput
                                    className="block w-full rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-pink-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-pink-500"
                                    onChange={(event) => setQuery(event.target.value)}
                                    onBlur={() => setQuery('')}
                                    displayValue={(date) => date?.name}
                                />
                                <ComboboxButton
                                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                                    <ChevronDownIcon className="size-5 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                </ComboboxButton>

                                {filteredDate && filteredDate.length > 0 && (
                                    <ComboboxOptions
                                        transition
                                        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
                                    >
                                        {query.length > 0 && (
                                            <ComboboxOption
                                                value={{id: null, name: query}}
                                                className="cursor-default px-3 py-2 text-gray-900 select-none data-focus:bg-pink-600 data-focus:text-white data-focus:outline-hidden dark:text-white dark:data-focus:bg-pink-500"
                                            >
                                                {query}
                                            </ComboboxOption>
                                        )}
                                        {filteredDate?.map((date) => (
                                            <ComboboxOption
                                                key={date.name}
                                                value={date}
                                                className="cursor-default px-3 py-2 text-gray-900 select-none data-focus:bg-pink-600 data-focus:text-white data-focus:outline-hidden dark:text-white dark:data-focus:bg-pink-500"
                                            >
                                                <div className="flex">
                                                    <span className="block truncate">{date.name}</span>
                                                    <span
                                                        className="ml-2 block truncate text-gray-500 in-data-focus:text-white dark:text-gray-400 dark:in-data-focus:text-white">
                                                        {date.username}
                                                    </span>
                                                </div>
                                            </ComboboxOption>
                                        ))}
                                    </ComboboxOptions>
                                )}
                            </div>
                        </Combobox>


                        <div className="space-y-6">
                        {/* Whole-account empty state (no months at all). */}
                        {hasNoMonths &&
                            <div className="rounded-xl bg-gray-50 dark:bg-gray-800 py-10 text-center ring-1 ring-gray-200 dark:ring-gray-700">
                                <p className="text-base font-medium text-gray-900 dark:text-gray-100">No purchase history yet</p>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Your VirtualTipz purchases will show up here.</p>
                            </div>}

                        {/* Month summary — total spent leads (this is a purchase history). */}
                        {buyHistory && monthPurchaseCount > 0 &&
                            <div className="rounded-xl bg-pink-600 px-6 py-5 shadow-sm">
                                <p className="text-sm font-semibold uppercase tracking-wide text-pink-100">
                                    {selectedDate?.name || "This month"}
                                </p>
                                <div className="mt-1 flex items-baseline justify-between">
                                    <span className="text-3xl font-bold text-white">
                                        <NumericFormat value={monthTotalSpent} displayType={'text'}
                                                       decimalScale={2} fixedDecimalScale={true}
                                                       thousandSeparator={true} prefix={'$'}/>
                                    </span>
                                    <span className="text-sm font-medium text-pink-100">
                                        <NumericFormat value={monthTotalVt} displayType={'text'} thousandSeparator={true}/> VT
                                        &nbsp;&middot;&nbsp;
                                        {monthPurchaseCount} {monthPurchaseCount === 1 ? "purchase" : "purchases"}
                                    </span>
                                </div>
                            </div>}

                        {/* Loading + per-month empty states. */}
                        {!buyHistory && !hasNoMonths && <HistorySkeleton/>}
                        {buyHistory && monthPurchaseCount === 0 && !hasNoMonths &&
                            <h3 className="text-sm text-gray-500 dark:text-gray-400">No purchases for this month.</h3>}

                        {/* Purchase cards, newest first. */}
                        {sortedHistory?.map((history) => (
                            <div key={history.id} className="overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700">
                                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 px-5 py-3">
                                    <div className="min-w-0">
                                        <p className="truncate font-semibold text-gray-900 dark:text-gray-100">{history.packageName}</p>
                                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{history.venueName}</p>
                                    </div>
                                    <p className="flex-shrink-0 pl-4 text-lg font-bold text-pink-600">
                                        <NumericFormat value={history.price} displayType={'text'}
                                                       decimalScale={2} fixedDecimalScale={true}
                                                       thousandSeparator={true} prefix={'$'}/>
                                    </p>
                                </div>
                                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 px-5 py-4 text-sm">
                                    <div>
                                        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Date placed</dt>
                                        <dd className="mt-0.5 text-gray-900 dark:text-gray-100">
                                            {new Date(history.purchaseDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </dd>
                                    </div>
                                    <div className="text-right">
                                        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">VT Amount</dt>
                                        <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-100">
                                            <NumericFormat value={history.virtualTips} displayType={'text'} thousandSeparator={true}/> VT
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
